/**
 * Mock user profile + per-account broker metadata for the Settings tab.
 *
 * Everything here is intentionally fake — the real WHB app exposes the
 * logged-in user's contact info (name, email, phone, address, trusted
 * contact) on an "Account details" surface. For the V2 redesign demo
 * we seed a plausible-looking persona instead of echoing Sergey's real
 * details so the screenshots are safe to share.
 *
 *   - `USER_PROFILE` is a single persona (one human, one identity).
 *   - `ACCOUNT_META` is keyed by `BrokerAccount.id` and holds broker-
 *     specific fields that the existing `BrokerAccount` shape doesn't
 *     carry — primarily the broker-side account UUID, the redacted
 *     account number, and the user-editable nickname.
 *
 * The broker IDs are deliberately shaped to resemble Alpaca's
 * `b87effeb-a8e9-41da-…` UUIDs and IBKR's "Uxxxxxxx" handles so the
 * Settings screen looks convincingly like a real broker admin panel.
 */

import { ACCOUNTS } from "./mock-data";

// ---- User persona --------------------------------------------------------

export interface UserProfile {
  name: string;
  email: string;
  emailVerified: boolean;
  phone: string;
  phoneVerified: boolean;
  address: {
    line1: string;
    line2?: string;
    cityStateZip: string;
  };
  /** Short descriptor rendered as the Investor Profile secondary line. */
  investorProfile: string;
  /** Null-as-em-dash: "None listed" when not set. */
  trustedContact: string | null;
}

/**
 * Fictional persona used for all Settings screens. Keeping this single
 * constant (rather than per-account) matches how real brokers think
 * about KYC — one user, many linked broker accounts, one set of contact
 * details shared across them.
 */
export const USER_PROFILE: UserProfile = {
  name: "Alex Morgan",
  email: "alex.morgan@fastmail.com",
  emailVerified: true,
  phone: "+1 (415) 555-0137",
  phoneVerified: true,
  address: {
    line1: "1428 Elm Street",
    line2: "Apt 4B",
    cityStateZip: "Portland, OR 97204"
  },
  investorProfile: "Moderate — Long-term growth",
  trustedContact: null
};

// ---- Per-account broker metadata -----------------------------------------

export interface AccountMeta {
  /** User-editable nickname. Defaults to the account's stock name. */
  nickname: string;
  /** Broker-side account UUID / handle. Mirrors the real WHB "Broker ID:" row. */
  brokerAccountId: string;
  /** Redacted account number (e.g. "····9027"). */
  accountNumberMasked: string;
  /** Upstream broker label — usually the same as BrokerAccount.provider. */
  brokerLabel: string;
  /** "Live" | "Paper" — derived from `paper` but kept here for display strings. */
  accountType: "Live" | "Paper";
}

/**
 * Seeded map from accountId → broker metadata. The UUID-looking strings
 * are static so the demo is stable across reloads; the last-4 matches
 * each account's existing `masked` field.
 */
export const ACCOUNT_META: Record<string, AccountMeta> = {
  "acc-live-1": {
    nickname: "Alpaca Live",
    brokerAccountId: "b87effeb-a8e9-41da-1b34-08dcf4116534",
    accountNumberMasked: "····9027",
    brokerLabel: "Alpaca",
    accountType: "Live"
  },
  "acc-live-2": {
    nickname: "IBKR Personal",
    brokerAccountId: "U7413920",
    accountNumberMasked: "····4418",
    brokerLabel: "Interactive Brokers",
    accountType: "Live"
  },
  "acc-1": {
    nickname: "WhiteHatBots (Paper)",
    brokerAccountId: "1b37af2f-4fc0-44c4-f56c-08de8daf51d8",
    accountNumberMasked: "····0001",
    brokerLabel: "WhiteHatBots",
    accountType: "Paper"
  },
  "acc-2": {
    nickname: "Alpaca Paper",
    brokerAccountId: "c1908fe4-6321-4b0e-a08c-0271a7f9b6d2",
    accountNumberMasked: "····5521",
    brokerLabel: "Alpaca",
    accountType: "Paper"
  },
  "acc-3": {
    nickname: "IBKR Paper",
    brokerAccountId: "DU8812430",
    accountNumberMasked: "····7730",
    brokerLabel: "Interactive Brokers",
    accountType: "Paper"
  }
};

/**
 * Safe lookup — falls back to a synthesized record derived from
 * `BrokerAccount` so new accounts added to mock-data don't blow up the
 * Settings tab. This mirrors the fallback pattern used elsewhere for
 * activities / positions when an account id isn't seeded.
 */
export function getAccountMeta(accountId: string): AccountMeta {
  const seeded = ACCOUNT_META[accountId];
  if (seeded) return seeded;

  const acc = ACCOUNTS.find((a) => a.id === accountId);
  return {
    nickname: acc?.name ?? "Account",
    brokerAccountId: accountId,
    accountNumberMasked: acc?.masked ?? "····0000",
    brokerLabel: acc?.provider ?? "Broker",
    accountType: acc?.paper ? "Paper" : "Live"
  };
}
